import { useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import type { Arbeitspaket } from '@pm/shared';
import { apiClient } from '../../../../api/client';
import { useArbeitspakete } from '../../../../api/hooks/useArbeitspakete';
import { unwrapBoards } from '../../../../api/hooks/useKanban';
import { aggregateClosedBoards } from './aggregateClosedBoards';
import { ClosedBoardsChart } from './ClosedBoardsChart';

/**
 * Lädt je Arbeitspaket alle Boards (inkl. eingefrorener) und zeigt das Balkendiagramm
 * der geschlossenen Boards. Query-Key und Abruf sind identisch zu useApKanbanBoards,
 * der Cache wird mit dem Kanban-Tab geteilt.
 */
export function ClosedBoardsSection({ projectId }: { projectId: string }) {
  const { data: apData } = useArbeitspakete(projectId);
  const aps = useMemo(
    () => (apData as unknown as { data?: Arbeitspaket[] } | undefined)?.data ?? [],
    [apData],
  );

  const results = useQueries({
    queries: aps.map((ap) => ({
      queryKey: ['projects', projectId, 'arbeitspakete', ap.id, 'kanban'] as const,
      queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/arbeitspakete/${ap.id}/kanban/boards`),
    })),
  });

  const loading = results.some((r) => r.isLoading);
  const boardsKey = results.map((r) => r.dataUpdatedAt).join('|');
  const { bars, legend } = useMemo(
    () => aggregateClosedBoards(
      results.flatMap((r) => unwrapBoards(r.data)),
      aps.map((a) => ({ id: a.id, nr: a.nr, titel: a.titel })),
    ),
    // results ist bei jedem Render ein neues Array; dataUpdatedAt zeigt echte Änderungen an.
    [boardsKey, aps],
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;
  }
  return <ClosedBoardsChart bars={bars} legend={legend} />;
}
